import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import AuthModal from '@/components/AuthModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { user, checkAuth } = useContext(AuthContext);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setFeaturedProducts(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  const handleAuthSuccess = () => {
    checkAuth();
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center grid-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-transparent to-black/50"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="float-animation mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_spring-mysql-shop/artifacts/w91br1kr_image.png" 
              alt="Skulls&Sheeps" 
              className="h-32 w-32 mx-auto mb-6 drop-shadow-2xl"
              data-testid="hero-logo"
            />
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent" data-testid="hero-title">
            Bienvenido a Skulls&Sheeps
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-400 mb-8 max-w-2xl mx-auto" data-testid="hero-subtitle">
            Descubre productos únicos con estilo oscuro y moderno. 
            Una experiencia de compra diferente.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/shop">
              <Button 
                size="lg" 
                className="btn-primary bg-purple-600 hover:bg-purple-700 text-white text-lg px-8 py-6 rounded-full"
                data-testid="explore-button"
              >
                Explorar Tienda <ArrowRight className="ml-2" />
              </Button>
            </Link>
            
            {!user && (
              <Button 
                onClick={() => setShowAuthModal(true)}
                size="lg" 
                variant="outline" 
                className="glass text-purple-400 border-purple-600 hover:bg-purple-600/10 text-lg px-8 py-6 rounded-full"
                data-testid="hero-login-button"
              >
                Iniciar Sesión
              </Button>
            )}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-600/10 rounded-full blur-3xl"></div>
      </section>
      
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-20 container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Sparkles className="text-purple-400 mr-2" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white" data-testid="featured-title">
                Productos Destacados
              </h2>
              <Sparkles className="text-purple-400 ml-2" />
            </div>
            <p className="text-gray-400" data-testid="featured-subtitle">Los más populares de nuestra colección</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop">
              <Button 
                size="lg" 
                variant="outline" 
                className="glass text-purple-400 border-purple-600 hover:bg-purple-600/10"
                data-testid="view-all-button"
              >
                Ver Todos los Productos <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20 bg-gradient-to-b from-transparent to-purple-900/10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass rounded-2xl p-8 text-center hover:scale-105 transition-transform">
              <div className="text-5xl mb-4">💀</div>
              <h3 className="text-xl font-bold text-white mb-2">Estilo Único</h3>
              <p className="text-gray-400">Diseños exclusivos que no encontrarás en otro lugar</p>
            </div>
            
            <div className="glass rounded-2xl p-8 text-center hover:scale-105 transition-transform">
              <div className="text-5xl mb-4">🐑</div>
              <h3 className="text-xl font-bold text-white mb-2">Calidad Premium</h3>
              <p className="text-gray-400">Productos seleccionados con los más altos estándares</p>
            </div>
            
            <div className="glass rounded-2xl p-8 text-center hover:scale-105 transition-transform">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-white mb-2">Entrega Rápida</h3>
              <p className="text-gray-400">Envío rápido y seguro a todo el mundo</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;